import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { GET_PORTFOLIO_TRANSACTIONS } from '../graphql/queries/transactions';
import { GET_PORTFOLIO_SUMMARY } from '../graphql/queries/portfolioSummary';
import { GET_NOTIFICATION_SETTINGS } from '../graphql/queries/notificationSettings';
import { useNavigate } from 'react-router-dom';
import { useOnboardingContext } from '../contexts/OnboardingContext';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import { useImportModal } from '../contexts/ImportModalContext';
import InvestmentGoalsModal from '../components/InvestmentGoalsModal';
import PortfolioManagementModal from '../components/PortfolioManagementModal';
import { SETUP_DEFAULT_EXIT_STRATEGIES } from '../graphql/mutations/setupExitStrategies';
import { toast } from 'react-hot-toast';
import EnableExitNotificationsModal from '../components/EnableExitNotificationsModal';

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { disableOnboarding } = useOnboardingContext();
  const { portfolios, currentPortfolioId, currentPortfolio } = usePortfolioContext();
  const [showPortfolioManager, setShowPortfolioManager] = useState(false);
  const { openImportModal } = useImportModal();

  const [activeStep, setActiveStep] = useState(1); // 1: create portfolio, 2: import tx, 3: set goals, 4: exit strategy, 5: notifications
  const [isGoalsOpen, setIsGoalsOpen] = useState(false);
  const { data: txData, refetch: refetchTx } = useQuery(GET_PORTFOLIO_TRANSACTIONS, {
    variables: { portfolioId: currentPortfolioId, limit: 1 },
    skip: !currentPortfolioId,
    fetchPolicy: 'cache-and-network'
  });
  const { data: summaryData, refetch: refetchSummary } = useQuery(GET_PORTFOLIO_SUMMARY, {
    variables: { portfolioId: currentPortfolioId },
    skip: !currentPortfolioId,
    fetchPolicy: 'cache-and-network'
  });

  const { data: notifData, refetch: refetchNotif } = useQuery(GET_NOTIFICATION_SETTINGS, {
    variables: { portfolioID: currentPortfolioId },
    skip: !currentPortfolioId,
    fetchPolicy: 'cache-and-network',
  });

  const [setupExitStrategies, { loading: settingExits }] = useMutation(SETUP_DEFAULT_EXIT_STRATEGIES, {
    onError: (e) => toast.error(e.message || 'Failed to set exit strategies'),
  });

  const [lastSetupCount, setLastSetupCount] = useState(null);
  const [isEnableNotifOpen, setIsEnableNotifOpen] = useState(false);

  // Refetch notification settings after enabling
  useEffect(() => {
    const handler = (e) => {
      const pid = e?.detail?.portfolioId;
      if (!currentPortfolioId || pid !== currentPortfolioId) return;
      try { refetchNotif(); } catch (_) {}
      // Advance to final step after enabling notifications if we are on step 5
      setActiveStep((s) => (s < 6 ? 6 : s));
    };
    window.addEventListener('notifications:updated', handler);
    return () => window.removeEventListener('notifications:updated', handler);
  }, [currentPortfolioId, refetchNotif]);

  // On initial load, set step based on existing data (portfolio, transactions, goal)
  const initializedStepRef = useRef(false);
  useEffect(() => {
    if (initializedStepRef.current) return;
    // Wait for a selected portfolio
    if (!currentPortfolioId) return;
    // Wait until queries have data states (or explicitly no data)
    const txTotal = txData?.transactions?.total;
    const goalMultipleRaw = summaryData?.portfolioSummary?.goal?.targetMultiple;
    if (txTotal === undefined || goalMultipleRaw === undefined) return;
    const hasTx = Number(txTotal) > 0;
    const goalMultiple = parseFloat(goalMultipleRaw);
    const hasGoal = !isNaN(goalMultiple) && goalMultiple > 0;
    let initial = 1;
    if (!hasTx) initial = 2;
    else if (hasGoal) initial = 4;
    else initial = 3;
    setActiveStep(initial);
    initializedStepRef.current = true;
  }, [currentPortfolioId, txData, summaryData]);

  // Auto-advance to step 2 after a portfolio becomes selected from step 1
  const prevPortfolioIdRef = useRef(currentPortfolioId);
  useEffect(() => {
    const prev = prevPortfolioIdRef.current;
    if (!prev && currentPortfolioId && activeStep === 1) {
      setActiveStep(2);
    }
    prevPortfolioIdRef.current = currentPortfolioId;
  }, [currentPortfolioId, activeStep]);

  // Fallback: auto-advance when portfolios length goes from 0 to >0
  const prevLenRef = useRef((Array.isArray(portfolios) ? portfolios.length : 0));
  useEffect(() => {
    const prevLen = prevLenRef.current || 0;
    const currLen = Array.isArray(portfolios) ? portfolios.length : 0;
    if (prevLen === 0 && currLen > 0 && activeStep === 1) {
      setActiveStep(2);
    }
    prevLenRef.current = currLen;
  }, [portfolios, activeStep]);

  // Advance immediately on portfolio:created event
  useEffect(() => {
    const handler = () => {
      if (activeStep === 1) setActiveStep(2);
    };
    window.addEventListener('portfolio:created', handler);
    return () => window.removeEventListener('portfolio:created', handler);
  }, [activeStep]);

  // Refetch transactions on global import event
  useEffect(() => {
    const handler = (e) => {
      const pid = e?.detail?.portfolioId;
      if (!currentPortfolioId || pid !== currentPortfolioId) return;
      try { refetchTx(); } catch (_) {}
    };
    window.addEventListener('transactions:imported', handler);
    return () => window.removeEventListener('transactions:imported', handler);
  }, [currentPortfolioId, refetchTx]);

  // Update goal info and advance to Step 4 when goal is set
  useEffect(() => {
    const handler = (e) => {
      const pid = e?.detail?.portfolioId;
      if (!currentPortfolioId || pid !== currentPortfolioId) return;
      try { refetchSummary(); } catch (_) {}
      if (activeStep === 3) setActiveStep(4);
    };
    window.addEventListener('goals:updated', handler);
    return () => window.removeEventListener('goals:updated', handler);
  }, [currentPortfolioId, refetchSummary, activeStep]);

  const handleSkip = () => {
    disableOnboarding();
    navigate('/');
  };

  return (
    <div className="max-w-3xl mx-auto py-8">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Welcome to MoonReady</h1>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Let's get your portfolio set up. We'll walk you through a few simple steps.
        </p>

        {/* Vertical steps list */}
        <div className="space-y-3">
          {/* Step 1 */}
          <div className={`rounded-lg border ${activeStep === 1 ? 'border-blue-300 dark:border-blue-700' : 'border-gray-200 dark:border-gray-700'}`}>
            <button
              className={`w-full text-left px-4 py-3 flex items-center gap-3 text-gray-800 dark:text-gray-100`}
              onClick={() => setActiveStep(1)}
            >
              <span className={`w-6 h-6 inline-flex items-center justify-center rounded-full text-xs ${activeStep === 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>1</span>
              <span>Create portfolio</span>
            </button>
            {activeStep === 1 && (
              <div className="px-4 pb-4">
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Your data in MoonReady is organized into portfolios. This helps you track
                  investments by year or season, separate strategies or risk levels, or any
                  grouping you prefer. Create your first portfolio to get started.
                </p>
                <div className="flex items-center gap-3">
                  <button onClick={() => setShowPortfolioManager(true)} className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700">Create portfolio</button>
                  {currentPortfolioId && (
                    <span className="text-sm text-gray-600 dark:text-gray-300">Selected: <span className="font-medium">{currentPortfolio?.name || 'Current portfolio'}</span></span>
                  )}
                </div>
              </div>
            )}
          </div>

          

          {/* Step 2 */}
          <div className={`rounded-lg border ${activeStep === 2 ? 'border-blue-300 dark:border-blue-700' : 'border-gray-200 dark:border-gray-700'}`}>
            <button
              className="w-full text-left px-4 py-3 flex items-center gap-3 text-gray-800 dark:text-gray-100"
              onClick={() => setActiveStep(2)}
            >
              <span className={`w-6 h-6 inline-flex items-center justify-center rounded-full text-xs ${activeStep === 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>2</span>
              <span>Import transactions</span>
            </button>
            {activeStep === 2 && (
              <div className="px-4 pb-4">
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Import your trading history to calculate positions and performance. You can upload CSV files, paste raw CSV data, or let AI process data in arbitrary formats (plain text, tables, lists).
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => openImportModal({ portfolioId: currentPortfolioId, defaultTab: 'ai', onSuccess: async () => {
                      try { await refetchTx(); } catch (_) {}
                    } })}
                    className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Import transactions
                  </button>
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Transactions in {currentPortfolio?.name || 'portfolio'}: {txData?.transactions?.total ?? 0}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Step 3 */}
          <div className={`rounded-lg border ${activeStep === 3 ? 'border-blue-300 dark:border-blue-700' : 'border-gray-200 dark:border-gray-700'}`}>
            <button
              className="w-full text-left px-4 py-3 flex items-center gap-3 text-gray-800 dark:text-gray-100"
              onClick={() => setActiveStep(3)}
            >
              <span className={`w-6 h-6 inline-flex items-center justify-center rounded-full text-xs ${activeStep === 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>3</span>
              <span>Set investment goal</span>
            </button>
            {activeStep === 3 && (
              <div className="px-4 pb-4">
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Define your target return multiple and preferred risk distribution to guide exit planning.
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setIsGoalsOpen(true)}
                    className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Set investment goal
                  </button>
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Goal: {summaryData?.portfolioSummary?.goal?.targetMultiple ? `${parseFloat(summaryData.portfolioSummary.goal.targetMultiple).toFixed(1)}x` : 'not set'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Step 4 */}
          <div className={`rounded-lg border ${activeStep === 4 ? 'border-blue-300 dark:border-blue-700' : 'border-gray-200 dark:border-gray-700'}`}>
            <button
              className="w-full text-left px-4 py-3 flex items-center gap-3 text-gray-800 dark:text-gray-100"
              onClick={() => setActiveStep(4)}
            >
              <span className={`w-6 h-6 inline-flex items-center justify-center rounded-full text-xs ${activeStep === 4 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>4</span>
              <span>Set exit strategy</span>
            </button>
            {activeStep === 4 && (
              <div className="px-4 pb-4">
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  You can automatically set exit levels for positions without an exit strategy, using default settings that depend on each asset's market cap.
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={async () => {
                      if (!currentPortfolioId) { toast.error('No portfolio selected'); return; }
                      try {
                        const { data } = await setupExitStrategies({ variables: { portfolioId: currentPortfolioId } });
                        const res = data?.setupDefaultExitStrategies;
                        if (res?.success) {
                          toast.success(`Exit strategies set for ${res.updatedCount} position(s)`);
                          setLastSetupCount(res.updatedCount ?? 0);
                          // Advance to notifications step regardless of count
                          setActiveStep(5);
                        } else {
                          toast.error(res?.error || 'Failed to set exit strategies');
                        }
                      } catch (_) {}
                    }}
                    disabled={settingExits}
                    className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {settingExits ? 'Setting…' : 'Set exit strategies'}
                  </button>
                  {lastSetupCount !== null && (
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Set for {lastSetupCount} position{lastSetupCount === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Step 5 */}
          <div className={`rounded-lg border ${activeStep === 5 ? 'border-blue-300 dark:border-blue-700' : 'border-gray-200 dark:border-gray-700'}`}>
            <button
              className="w-full text-left px-4 py-3 flex items-center gap-3 text-gray-800 dark:text-gray-100"
              onClick={() => setActiveStep(5)}
            >
              <span className={`w-6 h-6 inline-flex items-center justify-center rounded-full text-xs ${activeStep === 5 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>5</span>
              <span>Setup notifications</span>
            </button>
            {activeStep === 5 && (
              <div className="px-4 pb-4">
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Configure how you want to receive alerts about exit triggers and portfolio updates.
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={() => setIsEnableNotifOpen(true)}
                    className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Enable exit level notifications
                  </button>
                  {(() => {
                    const s = notifData?.notificationSettings;
                    const enabled = s?.telegramEnabled && s?.exitLevelNotifications && !!s?.telegramChatID;
                    const text = enabled ? 'Notifications enabled' : 'Notifications disabled';
                    return (
                      <span className="text-xs text-gray-600 dark:text-gray-400">{text}</span>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Step 6 */}
        <div className={`rounded-lg border ${activeStep === 6 ? 'border-blue-300 dark:border-blue-700' : 'border-gray-200 dark:border-gray-700'}`}>
            <button
              className="w-full text-left px-4 py-3 flex items-center gap-3 text-gray-800 dark:text-gray-100"
              onClick={() => setActiveStep(6)}
            >
              <span className={`w-6 h-6 inline-flex items-center justify-center rounded-full text-xs ${activeStep === 6 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>6</span>
              <span>Done!</span>
            </button>
            {activeStep === 6 && (
              <div className="px-4 pb-4">
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  All set. That's it for onboarding — welcome to MoonReady! Explore your dashboard, positions, and tools.
                </p>
              </div>
            )}
          </div>

        <div className="flex items-center mt-4 justify-between">
          <button
            onClick={handleSkip}
            className="px-4 py-2 text-sm rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Skip onboarding
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveStep((s) => Math.max(1, s - 1))}
              disabled={activeStep === 1}
              className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-50"
            >
              Prev
            </button>
            <button
              onClick={() => (activeStep === 6 ? handleSkip() : setActiveStep((s) => Math.min(6, s + 1)))}
              className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
            >
              {activeStep === 6 ? 'Complete' : 'Next'}
            </button>
          </div>
        </div>
        {/* Auto-advance to Step 3 when transactions count becomes > 0 */}
        {activeStep === 2 && (
          <AutoAdvanceWhenTxPresent total={txData?.transactions?.total ?? 0} onAdvance={() => setActiveStep(3)} />
        )}
        <InvestmentGoalsModal
          isOpen={isGoalsOpen}
          onClose={() => setIsGoalsOpen(false)}
          initialGoal={summaryData?.portfolioSummary?.goal}
        />
        {showPortfolioManager && (
          <PortfolioManagementModal
            isOpen={showPortfolioManager}
            onClose={() => setShowPortfolioManager(false)}
            defaultCreateOpen
          />
        )}
        <EnableExitNotificationsModal
          isOpen={isEnableNotifOpen}
          onClose={() => setIsEnableNotifOpen(false)}
          initialEnabled={notifData?.notificationSettings
            ? !!(notifData.notificationSettings.telegramEnabled && notifData.notificationSettings.exitLevelNotifications && notifData.notificationSettings.telegramChatID)
            : true}
          initialChatId={notifData?.notificationSettings?.telegramChatID || ''}
        />
      </div>
    </div>
  );
};

const AutoAdvanceWhenTxPresent = ({ total, onAdvance }) => {
  const prevRef = useRef(total);
  useEffect(() => {
    const prev = prevRef.current || 0;
    if (prev === 0 && total > 0) {
      onAdvance();
    }
    prevRef.current = total;
  }, [total, onAdvance]);
  return null;
};

export default OnboardingPage;


